class BcReviewCard {
  constructor(card) {
    this.card = card;
    this.id = card.dataset.reviewId;
    this.likesBase = parseInt(card.dataset.likesBase, 10) || 0;
    this.commentsBase = parseInt(card.dataset.commentsBase, 10) || 0;

    this.likeButton = card.querySelector('.vc-reviews__action--like');
    this.likesNumber = card.querySelector('.vc-reviews__likes-number');
    this.likesCountButton = card.querySelector('.vc-reviews__likes-count');

    this.commentButton = card.querySelector('.vc-reviews__action--comment');
    this.commentsCountButton = card.querySelector('.vc-reviews__comments-count');
    this.commentsNumber = card.querySelector('.vc-reviews__comments-number');
    this.commentsPanel = card.querySelector('.vc-reviews__comments');
    this.commentList = card.querySelector('.vc-reviews__comment-list');
    this.commentForm = card.querySelector('.vc-reviews__comment-form');
    this.commentInput = card.querySelector('.vc-reviews__comment-input');
    this.moreCommentsButton = card.querySelector('.vc-reviews__more-comments');

    this.shareButton = card.querySelector('.vc-reviews__action--share');
    this.shareLabel = this.shareButton ? this.shareButton.querySelector('.vc-reviews__action-label') : null;

    this.initLike();
    this.initComments();
    this.initShare();
  }

  storageKey(suffix) {
    return `vc-review-${this.id}-${suffix}`;
  }

  initLike() {
    if (!this.likeButton) return;
    const liked = localStorage.getItem(this.storageKey('liked')) === '1';
    this.setLikedState(liked);

    const toggle = () => {
      const nowLiked = localStorage.getItem(this.storageKey('liked')) !== '1';
      localStorage.setItem(this.storageKey('liked'), nowLiked ? '1' : '0');
      this.setLikedState(nowLiked);
    };

    this.likeButton.addEventListener('click', toggle);
    if (this.likesCountButton) this.likesCountButton.addEventListener('click', toggle);
  }

  setLikedState(liked) {
    this.likeButton.classList.toggle('is-liked', liked);
    this.likeButton.setAttribute('aria-pressed', liked ? 'true' : 'false');
    if (this.likesNumber) this.likesNumber.textContent = this.likesBase + (liked ? 1 : 0);
  }

  initComments() {
    if (!this.commentsPanel) return;

    const openPanel = () => {
      this.commentsPanel.hidden = false;
      if (this.commentInput) this.commentInput.focus();
    };

    const togglePanel = () => {
      this.commentsPanel.hidden = !this.commentsPanel.hidden;
      if (!this.commentsPanel.hidden && this.commentInput) this.commentInput.focus();
    };

    if (this.commentButton) this.commentButton.addEventListener('click', openPanel);
    if (this.commentsCountButton) this.commentsCountButton.addEventListener('click', togglePanel);

    if (this.moreCommentsButton) {
      this.moreCommentsButton.addEventListener('click', () => {
        this.commentList.querySelectorAll('.vc-reviews__comment--extra').forEach((item) => {
          item.hidden = false;
        });
        this.moreCommentsButton.remove();
      });
    }

    this.localComments = this.loadLocalComments();
    this.localComments.forEach((text) => this.renderComment('Vous', text));
    this.updateCommentsCount();

    if (this.commentForm) {
      this.commentForm.addEventListener('submit', (event) => {
        event.preventDefault();
        const text = (this.commentInput.value || '').trim();
        if (!text) return;
        this.localComments.push(text);
        this.saveLocalComments();
        this.renderComment('Vous', text);
        this.updateCommentsCount();
        this.commentInput.value = '';
      });
    }
  }

  loadLocalComments() {
    try {
      const raw = localStorage.getItem(this.storageKey('comments'));
      return raw ? JSON.parse(raw) : [];
    } catch (error) {
      return [];
    }
  }

  saveLocalComments() {
    localStorage.setItem(this.storageKey('comments'), JSON.stringify(this.localComments));
  }

  renderComment(author, text) {
    if (!this.commentList) return;
    const item = document.createElement('li');
    item.className = 'vc-reviews__comment vc-reviews__comment--local';
    const strong = document.createElement('strong');
    strong.textContent = author;
    const span = document.createElement('span');
    span.textContent = text;
    item.appendChild(strong);
    item.appendChild(span);
    this.commentList.appendChild(item);
  }

  updateCommentsCount() {
    if (this.commentsNumber) this.commentsNumber.textContent = this.commentsBase + this.localComments.length;
  }

  initShare() {
    if (!this.shareButton) return;
    this.shareButton.addEventListener('click', async () => {
      const shareData = {
        title: document.title,
        text: 'Découvrez VoirClaire',
        url: window.location.href,
      };
      if (navigator.share) {
        try {
          await navigator.share(shareData);
        } catch (error) {
          // visitor cancelled the native share sheet — nothing to do
        }
        return;
      }
      try {
        await navigator.clipboard.writeText(shareData.url);
        this.flashShareFeedback('Lien copié !');
      } catch (error) {
        this.flashShareFeedback('Copiez le lien depuis votre navigateur');
      }
    });
  }

  flashShareFeedback(message) {
    if (!this.shareLabel) return;
    const original = this.shareLabel.textContent;
    this.shareLabel.textContent = message;
    this.shareButton.disabled = true;
    setTimeout(() => {
      this.shareLabel.textContent = original;
      this.shareButton.disabled = false;
    }, 2000);
  }
}

class BcReviewsCarousel extends HTMLElement {
  constructor() {
    super();
    this.track = this.querySelector('.vc-reviews__track');
    this.cards = Array.from(this.querySelectorAll('.vc-reviews__card'));
    this.dots = Array.from(this.querySelectorAll('.vc-reviews__dot'));
    this.prevButton = this.querySelector('.vc-reviews__arrow--prev');
    this.nextButton = this.querySelector('.vc-reviews__arrow--next');

    if (!this.track || this.cards.length === 0) return;

    if (this.prevButton) this.prevButton.addEventListener('click', () => { this.scrollByCard(-1); this.restartAutoplay(); });
    if (this.nextButton) this.nextButton.addEventListener('click', () => { this.scrollByCard(1); this.restartAutoplay(); });

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.cards[index].scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
        this.restartAutoplay();
      });
    });

    if (this.dots.length) {
      this.observer = new IntersectionObserver(this.onIntersect.bind(this), {
        root: this.track,
        threshold: 0.6,
      });
      this.cards.forEach((card) => this.observer.observe(card));
    }

    this.cards.forEach((card) => new BcReviewCard(card));

    if (this.cards.length > 1 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.initAutoplay();
    }
  }

  initAutoplay() {
    this.autoplayDelay = 4000;
    this.playAutoplay();

    this.addEventListener('mouseenter', () => this.pauseAutoplay());
    this.addEventListener('mouseleave', () => this.playAutoplay());
    this.addEventListener('focusin', () => this.pauseAutoplay());
    this.addEventListener('focusout', () => this.playAutoplay());
    this.track.addEventListener('pointerdown', () => this.pauseAutoplay());
    this.track.addEventListener('pointerup', () => this.playAutoplay());
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.pauseAutoplay();
      else this.playAutoplay();
    });
  }

  playAutoplay() {
    this.pauseAutoplay();
    this.autoplayTimer = setInterval(() => this.advanceAutoplay(), this.autoplayDelay);
  }

  pauseAutoplay() {
    if (this.autoplayTimer) {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }
  }

  restartAutoplay() {
    if (this.autoplayTimer !== undefined) this.playAutoplay();
  }

  advanceAutoplay() {
    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    if (this.track.scrollLeft >= maxScroll - 4) {
      this.track.scrollTo({ left: 0, behavior: 'smooth' });
    } else {
      this.scrollByCard(1);
    }
  }

  scrollByCard(direction) {
    const cardWidth = this.cards[0].getBoundingClientRect().width;
    const gap = parseFloat(getComputedStyle(this.track).columnGap || 0);
    this.track.scrollBy({ left: direction * (cardWidth + gap), behavior: 'smooth' });
  }

  onIntersect(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const index = this.cards.indexOf(entry.target);
      if (index === -1) return;
      this.dots.forEach((dot) => dot.classList.remove('is-active'));
      this.dots[index].classList.add('is-active');
    });
  }
}

customElements.define('vc-reviews-carousel', BcReviewsCarousel);
